// src/modules/AuditEngine.tsx
import React, { useMemo, useState } from 'react';
import { User } from '../types';
import { AUDIT_TEMPLATES, KEY_QUESTION_META } from '../data/auditLibrary';
import { ActionPlan, AnswerValue, AuditQuestion, AuditRecord, AuditTemplate } from '../auditTypes';
import * as store from '../services/auditStore';

interface AnswerDraft { value: AnswerValue | null; note: string; }
interface PlanDraft { action: string; owner: string; priority: 'high' | 'medium' | 'low'; dueDate: string; }

const ANSWERS: { v: AnswerValue; label: string; cls: string }[] = [
  { v: 'yes', label: 'Yes', cls: 'bg-[#1B7F4D] text-white' },
  { v: 'partial', label: 'Partial', cls: 'bg-[#C77800] text-white' },
  { v: 'no', label: 'No', cls: 'bg-[#B3261E] text-white' },
  { v: 'na', label: 'N/A', cls: 'bg-[#6B7A76] text-white' },
];

export const AuditEngine: React.FC<{ user: User }> = ({ user }) => {
  const userName = (user as any).name || (user as any).email || 'User';
  const [tab, setTab] = useState<'schedule' | 'run' | 'history'>('schedule');
  const [, force] = useState(0);
  const refresh = () => force(x => x + 1);

  const records = store.getAuditRecords();
  const [running, setRunning] = useState<{ t: AuditTemplate; answers: Record<string, AnswerDraft>; plans: Record<string, PlanDraft> } | null>(null);
  const [summary, setSummary] = useState<{ score: number; plans: number } | null>(null);

  const start = (t: AuditTemplate) => {
    const answers: Record<string, AnswerDraft> = {};
    t.questions.forEach(q => (answers[q.id] = { value: null, note: '' }));
    setRunning({ t, answers, plans: {} });
    setTab('run');
  };

  const setAnswer = (q: AuditQuestion, v: AnswerValue) => {
    if (!running) return;
    const answers = { ...running.answers, [q.id]: { ...running.answers[q.id], value: v } };
    let plans = running.plans;
    if ((v === 'no' || v === 'partial') && !plans[q.id]) {
      const high = q.critical || v === 'no';
      plans = { ...plans, [q.id]: { action: '', owner: userName, priority: high ? 'high' : 'medium', dueDate: store.addDays(high ? 3 : 7) } };
    }
    setRunning({ ...running, answers, plans });
  };

  const complete = () => {
    if (!running) return;
    const { t, answers, plans } = running;
    for (const q of t.questions) {
      if (!answers[q.id].value) { alert(`Answer every question: "${q.text}"`); return; }
      if ((answers[q.id].value === 'no' || answers[q.id].value === 'partial')) {
        const p = plans[q.id];
        if (!p || !p.action.trim() || !p.owner.trim()) { alert(`Action plan required for: "${q.text}" – add action and owner.`); return; }
      }
    }
    const score = store.computeScore(answers, t.questions) ?? 0;
    const rec: AuditRecord = {
      id: store.uid(), templateId: t.id, templateName: t.name, frequency: t.frequency,
      dateISO: store.todayISO(), completedBy: userName,
      answers: Object.fromEntries(Object.entries(answers).map(([qid, a]) => [qid, { questionId: qid, value: a.value as AnswerValue, note: a.note }])),
      score, status: 'completed',
    };
    store.saveAuditRecord(rec);
    let count = 0;
    for (const q of t.questions) {
      const a = answers[q.id]; const p = plans[q.id];
      if ((a.value === 'no' || a.value === 'partial') && p) {
        const plan: ActionPlan = {
          id: store.uid(), auditRecordId: rec.id, auditName: t.name, questionId: q.id,
          issue: q.text + (a.note ? ` — Note: ${a.note}` : ''), action: p.action, owner: p.owner,
          priority: p.priority, raisedDate: store.todayISO(), dueDate: p.dueDate, status: 'open', keyQuestion: q.keyQuestion,
        };
        store.upsertActionPlan(plan); count++;
      }
    }
    setRunning(null); setSummary({ score, plans: count }); setTab('schedule'); refresh();
  };

  const dueCounts = useMemo(() => {
    let overdue = 0, due = 0;
    AUDIT_TEMPLATES.forEach(t => { const s = store.dueInfo(t, records).status; if (s === 'overdue') overdue++; else if (s === 'due') due++; });
    return { overdue, due };
  }, [records, tab]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#123B36]">Audit Engine</h1>
          <p className="text-sm text-[#6B7A76]">Every audit. Every answer. Every failure becomes a tracked action plan – closed loop.</p>
        </div>
        <div className="flex gap-2">
          {(['schedule', 'run', 'history'] as const).map(t2 => (
            <button key={t2} onClick={() => { if (t2 !== 'run' || running) setTab(t2); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${tab === t2 ? 'bg-[#123B36] text-white' : 'bg-white border border-[#E3DFD5] text-[#20302C]'}`}>
              {t2 === 'run' && running ? `Running: ${running.t.name}` : t2}
            </button>
          ))}
        </div>
      </div>

      {tab === 'schedule' && (
        <div className="space-y-6">
          <div className="flex gap-3">
            <div className="bg-[#B3261E] text-white rounded-xl px-4 py-2 text-sm font-semibold">{dueCounts.overdue} overdue</div>
            <div className="bg-[#C77800] text-white rounded-xl px-4 py-2 text-sm font-semibold">{dueCounts.due} due</div>
            <div className="bg-[#1B7F4D] text-white rounded-xl px-4 py-2 text-sm font-semibold">{AUDIT_TEMPLATES.length - dueCounts.overdue - dueCounts.due} up to date</div>
          </div>
          {(['daily', 'weekly', 'monthly'] as const).map(f => (
            <div key={f}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-[#6B7A76] mb-2">{f} audits</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {AUDIT_TEMPLATES.filter(t => t.frequency === f).map(t => {
                  const d = store.dueInfo(t, records);
                  const badge = d.status === 'overdue' ? 'bg-[#B3261E]' : d.status === 'due' ? 'bg-[#C77800]' : 'bg-[#1B7F4D]';
                  return (
                    <div key={t.id} className="bg-white rounded-xl border border-[#E3DFD5] p-4 flex items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-[#20302C]">{t.name}</div>
                        <div className="text-xs text-[#6B7A76] mt-0.5">{t.questions.length} questions · {d.label}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`${badge} text-white text-xs px-2 py-1 rounded-full capitalize`}>{d.status}</span>
                        <button onClick={() => start(t)} className="bg-[#123B36] text-white text-sm px-3 py-1.5 rounded-lg hover:opacity-90">Start</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'run' && running && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#E3DFD5] p-4 flex items-center justify-between">
            <div>
              <h2 className="font-bold text-[#123B36]">{running.t.name}</h2>
              <p className="text-xs text-[#6B7A76]">{running.t.description}</p>
            </div>
            <div className="text-sm text-[#6B7A76]">
              {Object.values(running.answers).filter(a => a.value).length}/{running.t.questions.length} answered
            </div>
          </div>
          {running.t.questions.map((q, i) => {
            const a = running.answers[q.id]; const p = running.plans[q.id];
            return (
              <div key={q.id} className="bg-white rounded-xl border border-[#E3DFD5] p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-[#20302C]">{i + 1}. {q.text}</div>
                    <div className="flex gap-2 mt-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ background: KEY_QUESTION_META[q.keyQuestion].color }}>{KEY_QUESTION_META[q.keyQuestion].label}</span>
                      {q.regulation && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E3DFD5] text-[#20302C]">{q.regulation}</span>}
                      {q.critical && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#B3261E] text-white">CRITICAL</span>}
                    </div>
                  </div>
                </div>
                <details className="text-xs text-[#6B7A76]"><summary className="cursor-pointer text-[#1D6FB8]">What CQC expects</summary><p className="mt-1">{q.guidance}</p></details>
                <div className="flex gap-2 flex-wrap">
                  {ANSWERS.map(o => (
                    <button key={o.v} onClick={() => setAnswer(q, o.v)}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium ${a.value === o.v ? o.cls : 'bg-[#F1EFE9] text-[#20302C] hover:bg-[#E3DFD5]'}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                <textarea value={a.note} placeholder="Notes / evidence (who, what, where)…"
                  onChange={e => setRunning({ ...running, answers: { ...running.answers, [q.id]: { ...a, note: e.target.value } } })}
                  className="w-full text-sm border border-[#E3DFD5] rounded-lg p-2 bg-[#FBFAF7]" rows={2} />
                {p && (
                  <div className="border-l-4 border-[#B3261E] bg-[#FBF3F2] rounded-r-lg p-3 space-y-2">
                    <div className="text-sm font-semibold text-[#B3261E]">⚠ Action plan required (closed loop)</div>
                    <input value={p.action} placeholder="What will you do to fix this?"
                      onChange={e => setRunning({ ...running, plans: { ...running.plans, [q.id]: { ...p, action: e.target.value } } })}
                      className="w-full text-sm border border-[#E3DFD5] rounded-lg p-2" />
                    <div className="grid grid-cols-3 gap-2">
                      <input value={p.owner} placeholder="Owner"
                        onChange={e => setRunning({ ...running, plans: { ...running.plans, [q.id]: { ...p, owner: e.target.value } } })}
                        className="text-sm border border-[#E3DFD5] rounded-lg p-2" />
                      <select value={p.priority}
                        onChange={e => setRunning({ ...running, plans: { ...running.plans, [q.id]: { ...p, priority: e.target.value as any } } })}
                        className="text-sm border border-[#E3DFD5] rounded-lg p-2">
                        <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                      </select>
                      <input type="date" value={p.dueDate}
                        onChange={e => setRunning({ ...running, plans: { ...running.plans, [q.id]: { ...p, dueDate: e.target.value } } })}
                        className="text-sm border border-[#E3DFD5] rounded-lg p-2" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <button onClick={complete} className="w-full bg-[#123B36] text-white font-semibold py-3 rounded-xl hover:opacity-90">
            Complete audit & raise action plans
          </button>
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-2">
          {[...records].sort((a, b) => b.dateISO.localeCompare(a.dateISO)).map(r => (
            <details key={r.id} className="bg-white rounded-xl border border-[#E3DFD5] p-4">
              <summary className="flex justify-between cursor-pointer">
                <span className="font-medium">{r.templateName}</span>
                <span className="text-sm text-[#6B7A76]">{store.fmtDate(r.dateISO)} · {r.completedBy} · <b className={r.score !== null && r.score < 70 ? 'text-[#B3261E]' : 'text-[#1B7F4D]'}>{r.score}%</b></span>
              </summary>
              <ul className="mt-3 text-sm space-y-1">
                {Object.values(r.answers).filter(a => a.value !== 'yes' && a.value !== 'na').map(a => {
                  const q = running?.t.questions.find(x => x.id === a.questionId);
                  return <li key={a.questionId} className="text-[#6B7A76]">• {a.value === 'no' ? '❌' : '◐'} {a.note || a.questionId}</li>;
                })}
                {Object.values(r.answers).every(a => a.value === 'yes' || a.value === 'na') && <li className="text-[#1B7F4D]">All compliant ✔</li>}
              </ul>
            </details>
          ))}
          {records.length === 0 && <p className="text-sm text-[#6B7A76]">No audits completed yet.</p>}
        </div>
      )}

      {summary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md text-center space-y-3">
            <div className={`text-5xl font-bold ${summary.score >= 85 ? 'text-[#1B7F4D]' : summary.score >= 70 ? 'text-[#C77800]' : 'text-[#B3261E]'}`}>{summary.score}%</div>
            <p className="text-[#20302C] font-medium">Audit saved.</p>
            <p className="text-sm text-[#6B7A76]">{summary.plans === 0 ? 'No failures – nothing to action. ✔' : `${summary.plans} action plan(s) raised and now tracked to closure.`}</p>
            <button onClick={() => setSummary(null)} className="bg-[#123B36] text-white px-6 py-2 rounded-lg">Continue</button>
          </div>
        </div>
      )}
    </div>
  );
};